// CompletionStep Component
// Screen 5: Celebrate completion and show next steps

import React, { useEffect, useState } from 'react';
import { FaCheckCircle, FaUser, FaHome, FaTrophy } from 'react-icons/fa';
import styles from './CompletionStep.module.css';

const CompletionStep = ({ formData, onViewProfile, onGoToDashboard }) => {
  const [showConfetti, setShowConfetti] = useState(false);

  useEffect(() => {
    // Trigger confetti animation
    setShowConfetti(true);
    const timer = setTimeout(() => setShowConfetti(false), 3000);
    return () => clearTimeout(timer);
  }, []);

  const calculateCompleteness = () => {
    let score = 0;
    const isParent = formData.userRole === 'parent';

    // Basic info (parent: 60 pts, others: 40 pts)
    const basicWeight = isParent ? 15 : 10;
    if (formData.firstName && formData.lastName) score += basicWeight;
    if (formData.dateOfBirth) score += basicWeight;
    if (formData.gender) score += basicWeight;
    if (formData.location?.city && formData.location?.country) score += basicWeight;

    if (!isParent) {
      // Athletic info (40 points)
      if (formData.primarySport) score += 15;
      if (formData.positions?.length > 0) score += 10;
      if (formData.currentLevel) score += 15;
    } else {
      // For parents, athletic is optional bonus
      if (formData.primarySport) score += 5;
      if (formData.positions?.length > 0) score += 5;
      if (formData.currentLevel) score += 5;
    }

    // Bio (parent: 25 pts, others: 20 pts)
    const bioWeight = isParent ? 12.5 : 10;
    if (formData.headline) score += bioWeight;
    if (formData.bio && formData.bio.length >= 100) score += bioWeight;

    return Math.min(Math.round(score), 100);
  };

  const completeness = calculateCompleteness();

  return (
    <div className={styles.container}>
      {showConfetti && <div className={styles.confetti}>🎉</div>}

      <div className={styles.content}>
        <div className={styles.successIcon}>
          <FaCheckCircle />
        </div>

        <h1 className={styles.title}>Profile Created Successfully!</h1>
        <p className={styles.subtitle}>
          {formData.userRole === 'parent'
            ? `Welcome to SportLoop, ${formData.firstName}! Your parent profile is ready.`
            : `Welcome to SportLoop, ${formData.firstName}! Your athletic journey starts here.`
          }
        </p>

        {/* Profile Completeness */}
        <div className={styles.completenessCard}>
          <div className={styles.completenessHeader}>
            <h3>Profile Completeness</h3>
            <span className={styles.percentage}>{completeness}%</span>
          </div>
          <div className={styles.progressBar}>
            <div 
              className={styles.progressFill}
              style={{ width: `${completeness}%` }}
            />
          </div>
          <p className={styles.completenessNote}>
            {completeness >= 80 
              ? '🌟 Great job! Your profile is looking strong.'
              : '💡 Complete your profile to increase visibility with clubs and scouts.'
            }
          </p>
        </div>

        {/* Profile Preview */}
        <div className={styles.profilePreview}>
          <h3>Your Profile Snapshot</h3>
          <div className={styles.previewCard}>
            <div className={styles.previewHeader}>
              <div className={styles.previewAvatar}>
                {formData.firstName?.charAt(0)}{formData.lastName?.charAt(0)}
              </div>
              <div className={styles.previewInfo}>
                <h4>{formData.firstName} {formData.lastName}</h4>
                <p>
                  {formData.userRole === 'parent' && <span className={styles.parentBadge}>Parent Profile</span>}
                  {formData.headline || (formData.primarySport
                    ? `${formData.primarySport} ${formData.positions[0] || 'Player'}`
                    : formData.userRole === 'parent' ? '' : 'Player'
                  )}
                </p>
              </div>
            </div>
            <div className={styles.previewDetails}>
              <div className={styles.previewItem}>
                <span className={styles.previewLabel}>Role:</span>
                <span className={styles.previewValue}>
                  {formData.userRole === 'parent' ? 'Parent of a Player' :
                   formData.userRole === 'coach' ? 'Coach' :
                   formData.userRole === 'club_owner' ? 'Club Owner' : 'Player'}
                </span>
              </div>
              {formData.primarySport && (
                <div className={styles.previewItem}>
                  <span className={styles.previewLabel}>Sport:</span>
                  <span className={styles.previewValue}>{formData.primarySport}</span>
                </div>
              )}
              {formData.positions?.length > 0 && (
                <div className={styles.previewItem}>
                  <span className={styles.previewLabel}>Position:</span>
                  <span className={styles.previewValue}>{formData.positions.join(', ')}</span>
                </div>
              )}
              {formData.currentLevel && (
                <div className={styles.previewItem}>
                  <span className={styles.previewLabel}>Level:</span>
                  <span className={styles.previewValue}>
                    {formData.currentLevel.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())}
                  </span>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Next Steps */}
        <div className={styles.nextSteps}>
          <h3>What's Next?</h3>
          <div className={styles.steps}>
            <div className={styles.stepItem}>
              <FaTrophy className={styles.stepIcon} />
              <div className={styles.stepText}>
                <h4>Enhance Your Profile</h4>
                <p>Add highlights, stats, and media</p>
              </div>
            </div>
            <div className={styles.stepItem}>
              <FaUser className={styles.stepIcon} />
              <div className={styles.stepText}>
                <h4>Connect with Clubs</h4>
                <p>Apply to programs and teams</p>
              </div>
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className={styles.buttons}>
          <button className={styles.primaryButton} onClick={onViewProfile}>
            <FaUser />
            View My Profile
          </button>
          <button className={styles.secondaryButton} onClick={onGoToDashboard}>
            <FaHome />
            Go to Dashboard
          </button>
        </div>
      </div>
    </div>
  );
};

export default CompletionStep;















